import React, { useId } from 'react'
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipToggle,
} from '@mc/design-system'
import { useAnnualPass } from '@mc/growth'
import { useTranslation } from '@mc/i18n'
import { Locations } from '@mc/track-event'
import { trackCtaPurchaseClick } from '../../events'

type TooltipCtaProps = {
  disabled: boolean
  fullWidth?: boolean
  href: string
  action: 'Submit My Picks' | 'Continue'
  onClick(): void
  children: string
  location?: typeof Locations.HERO | typeof Locations.QUIZ_MODULE
}

export const TooltipCta = ({
  disabled,
  fullWidth = false,
  href,
  action,
  onClick,
  children,
  location,
}: TooltipCtaProps) => {
  const { t } = useTranslation('@mc/next')
  const annualPass = useAnnualPass()
  const disabledDescriptionId = useId()
  const disabledTooltip = t('home.hero.cta.disabledTooltip')

  const handleClick = (e: React.MouseEvent) => {
    if (disabled) {
      e.preventDefault()
      return
    }
    trackCtaPurchaseClick({ action, href, location, annualPass })
    onClick()
  }

  return (
    <Tooltip>
      <TooltipToggle>
        <Button
          // a11y link disabling: https://a11y-guidelines.orange.com/en/articles/disable-elements/#disable-a-link
          // eslint-disable-next-line jsx-a11y/anchor-is-valid -- href is intentionally omitted while disabled (see link above) to make the anchor non-navigable
          render={<a href={disabled ? undefined : href} />}
          role='link'
          aria-disabled={disabled ? true : undefined}
          accessibleWhenDisabled
          // the href-less disabled anchor is not tabbable without an explicit tabIndex
          tabIndex={disabled ? 0 : undefined}
          aria-describedby={disabled ? disabledDescriptionId : undefined}
          onClick={handleClick}
          fullWidth={fullWidth}
        >
          {children}
        </Button>
      </TooltipToggle>
      {disabled && (
        <>
          <span id={disabledDescriptionId} className='mc-sr-only'>
            {disabledTooltip}
          </span>
          <TooltipContent>{disabledTooltip}</TooltipContent>
        </>
      )}
    </Tooltip>
  )
}
